import { useState, useMemo } from "react";
import { ThermalSettingsIcon, DividerLine } from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { useThermalDesign } from "../../../ThermalDesignContext";
import { useSelectedFace } from "./visualization/useSelectedFace";
import { FACE_TO_DECK_MAP } from "../types/subsystems";
import { useThermalCoatingToggleDisabled, useSubsystemsToggleDisabled } from "../hooks/ThermalDesignerHooks";
import "../styles/ThermalDesigner.scss";
import CustomToolTip from "../../../../../../CommonComponents/CustomToolTip";
import LayersPopup from "../../../../../../CommonComponents/LayersPopup/LayersPopup";

const LAYER_ITEMS = [
  { id: "orientation_gizmo", label: "3D Orientation Gizmo", defaultChecked: true },
  { id: "nodes", label: "Nodes", defaultChecked: false  },
  { id: "nadir_axis", label: "Nadir Axis", defaultChecked: false },
  { id: "roll_pitch_yaw", label: "RPY Axis", defaultChecked: false },
  { id: "thermal_coating_view", label: "Thermal Coatings View", defaultChecked: false },
  { id: "subsystems", label: "Subsystems", defaultChecked: false },
];

const ThermalSettingsBtn = () => {
  const [layers, setLayers] = useState(
    LAYER_ITEMS.map((item) => ({ ...item, checked: item.defaultChecked ?? true }))
  );
  const { 
    setShowNodes, 
    setShowAxisLabels, 
    setShowNadirLabel, 
    setShowOrientationGizmo, 
    setShowCoatingView, 
    setShowSubsystemView,
    subsystemInstances,
    showCoatingView,
    showSubsystemView
  } = useThermalDesign();
  
  const selectedFace = useSelectedFace();
  const isThermalCoatingDisabled = useThermalCoatingToggleDisabled();

  // Compute visible subsystems count for disable check
  const selectedDeck = useMemo(() => {
    if (!selectedFace) return null;
    return FACE_TO_DECK_MAP[selectedFace] || null;
  }, [selectedFace]);

  const visibleSubsystemsCount = useMemo(() => {
    if (!selectedDeck) return 0;
    return subsystemInstances.filter((instance) => instance.mountLocation.deck === selectedDeck && instance.enabled).length;
  }, [subsystemInstances, selectedDeck]);

  const isSubsystemsDisabled = useSubsystemsToggleDisabled(visibleSubsystemsCount);

  // Sync layer state with context state for thermal views
  const syncedLayers = layers.map((l) => {
    if (l.id === "thermal_coating_view") return { ...l, checked: showCoatingView };
    if (l.id === "subsystems") return { ...l, checked: showSubsystemView };
    return l;
  });

  const toggleLayer = (id: string) => {
    setLayers((prev) => {
      const updated = prev.map((l) => (l.id === id ? { ...l, checked: !l.checked } : l));
      if (id === "nodes") {
        setShowNodes(updated.find((l) => l.id === "nodes")?.checked ?? true);
      }
      if (id === "roll_pitch_yaw") {
        setShowAxisLabels(updated.find((l) => l.id === "roll_pitch_yaw")?.checked ?? true);
      }
      if (id === "nadir_axis") {
        setShowNadirLabel(updated.find((l) => l.id === "nadir_axis")?.checked ?? false);
      }
      if (id === "orientation_gizmo") {
        const gizmoNowOn = updated.find((l) => l.id === "orientation_gizmo")?.checked ?? true;
        setShowOrientationGizmo(gizmoNowOn);
        if (gizmoNowOn) {
          setShowAxisLabels(false);
          setShowNadirLabel(false);
        } else {
          setShowAxisLabels(updated.find((l) => l.id === "roll_pitch_yaw")?.checked ?? false);
          setShowNadirLabel(updated.find((l) => l.id === "nadir_axis")?.checked ?? false);
        }
      }
      // Independent toggling for thermal_coating_view - syncs with context
      if (id === "thermal_coating_view") {
        const coatingNowOn = updated.find((l) => l.id === "thermal_coating_view")?.checked ?? false;
        setShowCoatingView(coatingNowOn);
      }
      // Independent toggling for subsystems - syncs with context
      if (id === "subsystems") {
        const subsystemsNowOn = updated.find((l) => l.id === "subsystems")?.checked ?? false;
        setShowSubsystemView(subsystemsNowOn);
      }
      return updated;
    });
  };

  const gizmoOn = syncedLayers.find((l) => l.id === "orientation_gizmo")?.checked ?? false;
  const layerItems = syncedLayers.map((l) => {
    let disabled = gizmoOn && (l.id === "nadir_axis" || l.id === "roll_pitch_yaw");
    if (l.id === "thermal_coating_view") disabled = isThermalCoatingDisabled;
    if (l.id === "subsystems") disabled = isSubsystemsDisabled;
    return {
      id: l.id,
      label: l.label,
      checked: l.checked,
      disabled,
    };
  });

  return (
    <div className="thermal-settings-btn-wrapper">
      <LayersPopup
        items={layerItems}
        onToggle={toggleLayer}
        placement="left"
        imgSize={16}
        headerDivider={
          <div className="nodeSpecsModal__divider__div">
            <DividerLine />
          </div>
        }
        renderTrigger={({ isOpen, onToggle }) => (
          <CustomToolTip title="Thermal Display Settings" placement="top">
            <div
              className={`thermal-settings-btn${isOpen ? " thermal-settings-btn--active" : ""}`}
              onClick={onToggle}
            >
              <button className="thermal-settings-btn__button">
                <ThermalSettingsIcon isActive={isOpen} />
              </button>
            </div>
          </CustomToolTip>
        )}
      />
    </div>
  );
};

export default ThermalSettingsBtn;
